import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Hand, Loader2, ExternalLink, X, AlertTriangle, Trash2, Smartphone, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export function TakeoverButton({ agentId, runtime, vmosPadCode }) {
  // Mobile-runtime agents use a screenshot-polling viewer instead of noVNC,
  // because the device lives on VMOS Cloud (no Xvfb/x11vnc available).
  if (runtime === 'mobile') {
    return <MobileTakeoverButton agentId={agentId} padCode={vmosPadCode} />;
  }
  return <BrowserTakeoverButton agentId={agentId} />;
}

function BrowserTakeoverButton({ agentId }) {
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [duration, setDuration] = React.useState(30);
  const [fresh, setFresh] = React.useState(false);
  // Danger-Zone gate: user must explicitly toggle this to reveal destructive actions.
  const [dangerOpen, setDangerOpen] = React.useState(false);

  // Reset transient state whenever dialog re-opens.
  React.useEffect(() => {
    if (!open) {
      setFresh(false);
      setDangerOpen(false);
    }
  }, [open]);

  const { data, refetch } = useQuery({
    queryKey: ['takeover', agentId],
    queryFn: () => api.get(`/agents/${agentId}/takeover`).then(r => r.data),
    refetchInterval: open ? 3000 : false,
    enabled: open,
  });

  const { data: agentData } = useQuery({
    queryKey: ['agent', agentId],
    queryFn: () => api.get(`/agents/${agentId}`).then(r => r.data),
    enabled: open,
    staleTime: 15_000,
  });
  const agent = agentData?.agent || agentData || {};
  const mailbox = (agent.config || {}).factory_mailbox || null;

  const startMut = useMutation({
    mutationFn: (body) => api.post(`/agents/${agentId}/takeover`, body).then(r => r.data),
    onSuccess: () => { toast.success('Takeover queued', { description: 'Camoufox launching under Xvfb (~30s)…' }); refetch(); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to start'),
  });
  const cancelMut = useMutation({
    mutationFn: () => api.delete(`/agents/${agentId}/takeover`).then(r => r.data),
    onSuccess: () => {
      toast.success('Takeover ended', { description: 'Worker will tear down Camoufox + Xvfb in ~3s.' });
      setOpen(false);
      qc.invalidateQueries({ queryKey: ['takeover', agentId] });
      qc.invalidateQueries({ queryKey: ['agent', agentId] });
    },
  });
  const clearProfileMut = useMutation({
    mutationFn: () => api.post(`/agents/${agentId}/clear-profile`).then(r => r.data),
    onSuccess: () => { toast.success('Profile cleared', { description: 'R2 blob + seed cookies + local dir wiped. Next task starts clean.' }); qc.invalidateQueries({ queryKey: ['agent', agentId] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Clear failed'),
  });

  const session = data?.session;

  const copyText = async (label, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`Copied ${label}`);
    } catch {
      toast.error('Clipboard blocked — select/copy manually');
    }
  };

  // Fresh-start safeguard: instead of a plain checkbox, ask the user to type
  // a confirmation phrase. Prevents an accidental tick from nuking a working
  // (logged-in) Reddit session at the start of every takeover.
  const onToggleFresh = (next) => {
    if (!next) { setFresh(false); return; }
    const ok = window.prompt(
      'Fresh start will IGNORE every saved cookie + cache on this agent.\n\n' +
      'You will lose your Reddit login session. Only do this if Reddit is ' +
      'actively blocking you ("whoa there, pardner!").\n\n' +
      'Type FRESH to confirm:'
    );
    if ((ok || '').trim().toUpperCase() === 'FRESH') {
      setFresh(true);
      toast.warning('Fresh-start enabled', { description: 'Saved cookies will be ignored for the next takeover.' });
    } else {
      setFresh(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm" data-testid="takeover-button"><Hand className="mr-2 h-4 w-4" /> Human takeover</Button>
      </DialogTrigger>
      <DialogContent className={session ? 'max-w-5xl' : 'max-w-md'} data-testid="takeover-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Hand className="h-4 w-4 text-[hsl(var(--primary))]" /> Human takeover</DialogTitle>
          <DialogDescription>
            Drive the agent's browser manually — great for solving a CAPTCHA, logging in once, or verifying a tricky interaction.
            Whatever you do persists into the agent's profile for future automated runs.
          </DialogDescription>
        </DialogHeader>

        {mailbox?.email && (
          <div
            className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-[11px] space-y-2"
            data-testid="takeover-factory-mailbox"
          >
            <div className="font-medium text-amber-100">
              Factory mailbox (not auto-logged-in)
            </div>
            <div className="text-[hsl(var(--text-3))]">
              Signup did not finish a Reddit session. Credential stays empty until
              verify succeeds. Use these Tuta creds inside the remote browser
              (paste via the bar under the VNC view — host Ctrl+V into the remote
              field often does not work).
            </div>
            <div className="flex flex-wrap items-center gap-2 font-mono text-[hsl(var(--text-1))]">
              <span>{mailbox.email}</span>
              <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-[10px]"
                      onClick={() => copyText('email', mailbox.email)}>Copy email</Button>
              {mailbox.password && (
                <>
                  <span className="opacity-70">{mailbox.password}</span>
                  <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-[10px]"
                          onClick={() => copyText('password', mailbox.password)}>Copy password</Button>
                  <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-[10px]"
                          onClick={() => copyText('email:password', `${mailbox.email}:${mailbox.password}`)}>
                    Copy both
                  </Button>
                </>
              )}
            </div>
            {mailbox.stage && (
              <div className="text-[10px] text-[hsl(var(--text-3))]">Last stage: {mailbox.stage}</div>
            )}
          </div>
        )}

        {!session ? (
          <div className="space-y-3">
            <div className="rounded-md border border-white/5 bg-[hsl(var(--surface-2))] p-3 text-xs text-[hsl(var(--text-3))]">
              <div className="font-medium text-[hsl(var(--text-2))] mb-1">How it works</div>
              1. Worker spawns Xvfb + Camoufox (non-headless) + x11vnc + websockify inside its container.<br />
              2. Pre-flight: Camoufox navigates to <code className="font-mono text-[10px]">api.ipify.org</code> FIRST and logs the external IP — so you can verify proxy egress.<br />
              3. You get a same-origin noVNC viewer. Paste into the bar under the screen, then Ctrl+V in the remote field (host Ctrl+V into the remote page often does not work).<br />
              4. Factory agents open Tuta when a mailbox was minted — they are not logged into Reddit until verify completes. Session auto-ends after your duration; profile persists to R2.
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <label className="text-sm">Duration (min):</label>
                <input
                  type="number" min={5} max={60} value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  className="w-20 rounded bg-[hsl(var(--surface-2))] border border-white/10 px-2 py-1 text-sm tabular"
                  data-testid="takeover-duration-input"
                />
              </div>
            </div>

            <DangerZone
              open={dangerOpen}
              onToggle={() => setDangerOpen(v => !v)}
              fresh={fresh}
              onToggleFresh={onToggleFresh}
              onClearProfile={() => {
                const ok = window.prompt(
                  'This will DELETE the persisted Camoufox profile (R2 + seed cookies + local dir) for this agent.\n\n' +
                  'You will lose every saved Reddit cookie, session, and warmup score. The proxy and credential are preserved.\n\n' +
                  'Type DELETE to confirm:'
                );
                if ((ok || '').trim().toUpperCase() === 'DELETE') clearProfileMut.mutate();
              }}
              clearPending={clearProfileMut.isPending}
            />

            <DialogFooter>
              <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={() => startMut.mutate({ duration_minutes: duration, fresh })} disabled={startMut.isPending} data-testid="takeover-start-button">
                {startMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Start takeover{fresh ? ' (FRESH ⚠)' : ''}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-[hsl(var(--text-3))]">
                Session expires in {Math.max(0, Math.round((session.expires_at * 1000 - Date.now()) / 60000))} min
                {session.raw_url && (
                  <> · <a className="underline text-[hsl(var(--primary))]" href={session.raw_url} target="_blank" rel="noreferrer">Open raw stream <ExternalLink className="inline h-3 w-3" /></a></>
                )}
              </div>
              <Button variant="destructive" size="sm" onClick={() => cancelMut.mutate()} disabled={cancelMut.isPending} data-testid="takeover-cancel-button">
                <X className="mr-1 h-3.5 w-3.5" /> End session
              </Button>
            </div>
            <ExternalIPReadout taskId={session.task_id} />
            <div className="rounded-md border border-white/10 bg-black overflow-hidden" style={{ height: '60vh' }}>
              <iframe
                src={`${process.env.REACT_APP_BACKEND_URL}${session.viewer_url || session.url}`}
                title="noVNC takeover"
                className="w-full h-full"
                allow="clipboard-read; clipboard-write"
                data-testid="takeover-iframe"
              />
            </div>
            <div className="text-[11px] text-[hsl(var(--text-3))] font-mono break-all">
              Viewer: {session.viewer_url || session.url}
              {session.raw_url && session.viewer_url && (
                <div className="mt-0.5 opacity-60">Raw (HTTP, mixed-content blocked from HTTPS): {session.raw_url}</div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Danger zone ---------- */
function DangerZone({ open, onToggle, fresh, onToggleFresh, onClearProfile, clearPending }) {
  return (
    <div className="rounded-md border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/5">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 text-left"
        data-testid="danger-zone-toggle"
      >
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--danger))]">
          <AlertTriangle className="h-3.5 w-3.5" /> Danger zone
        </span>
        <span className="text-[10px] text-[hsl(var(--text-3))]">{open ? 'hide' : 'show'}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          <label className="flex items-start gap-2 cursor-pointer rounded-md border border-[hsl(var(--danger))]/30 bg-black/20 p-2.5"
                  data-testid="fresh-mode-toggle">
            <input
              type="checkbox"
              checked={fresh}
              onChange={(e) => onToggleFresh(e.target.checked)}
              className="mt-0.5 accent-red-500"
            />
            <div>
              <div className="text-sm font-medium text-[hsl(var(--text-1))] flex items-center gap-1.5">
                Fresh start <span className="text-[10px] uppercase tracking-wide text-[hsl(var(--danger))]">destructive</span>
              </div>
              <div className="text-[11px] text-[hsl(var(--text-3))] mt-0.5">
                Ignores any saved cookies/cache for this takeover only. Use ONLY when "whoa there, pardner!" keeps appearing — Reddit blocks are sticky to the profile, and a clean slate bypasses it. You will be logged out of Reddit.
              </div>
            </div>
          </label>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearProfile}
            disabled={clearPending}
            data-testid="clear-profile-button"
            className="text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))]/10 hover:text-[hsl(var(--danger))]"
          >
            {clearPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Clear saved profile (hard reset)
          </Button>
          <div className="text-[10px] text-[hsl(var(--text-3))] -mt-1.5 leading-relaxed">
            Permanently deletes the R2 profile blob, seed cookies, and the local Camoufox dir. Proxy and bound credential are preserved. Cannot be undone.
          </div>
        </div>
      )}
    </div>
  );
}

function ExternalIPReadout({ taskId }) {
  const { data } = useQuery({
    queryKey: ['task-events', taskId],
    queryFn: () => api.get(`/tasks/${taskId}`).then(r => r.data),
    refetchInterval: 5000,
    enabled: !!taskId,
  });
  const evts = data?.events || [];
  const ipEvent = evts.find(e => e.kind === 'takeover.external_ip');
  const failEvent = evts.find(e => e.kind === 'takeover.external_ip_probe_failed');
  const rotated = evts.some(e => e.kind === 'takeover.proxy_session_rotate');
  const freshEvent = evts.some(e => e.kind === 'takeover.fresh_profile');
  if (!ipEvent && !failEvent) {
    return (
      <div className="rounded-md border border-white/5 bg-[hsl(var(--surface-2))] p-2 text-[11px] text-[hsl(var(--text-3))] font-mono" data-testid="external-ip-readout">
        Resolving external IP via api.ipify.org…
      </div>
    );
  }
  if (!ipEvent && failEvent) {
    const err = failEvent.payload?.error || 'proxy egress failed';
    return (
      <div className="rounded-md border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10 p-2 text-[11px] text-[hsl(var(--danger))] font-mono space-y-1" data-testid="external-ip-readout">
        <div>Browser egress failed — VNC is up but the proxy cannot open websites.</div>
        <div className="text-[hsl(var(--text-3))] break-all">{err}</div>
        {failEvent.payload?.proxy_endpoint && (
          <div className="text-[hsl(var(--text-3))]">Proxy: {failEvent.payload.proxy_endpoint}</div>
        )}
        {rotated && <div className="text-[hsl(var(--warning))]">Retried with a fresh Decodo session id — still failing.</div>}
      </div>
    );
  }
  const p = ipEvent.payload || {};
  return (
    <div className="rounded-md border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5 p-2 text-[11px] font-mono flex flex-wrap gap-x-4 gap-y-1" data-testid="external-ip-readout">
      <span>Egress IP: <span className="text-[hsl(var(--success))] font-semibold">{p.ip}</span></span>
      <span>Proxy: {p.via_proxy ? 'YES' : 'NO (direct)'} {p.proxy_endpoint && `→ ${p.proxy_endpoint}`}</span>
      {p.browser_timezone && <span data-testid="browser-tz">TZ: <span className="text-[hsl(var(--text-1))]">{p.browser_timezone}</span></span>}
      {p.browser_locale && <span data-testid="browser-locale">Locale: <span className="text-[hsl(var(--text-1))]">{p.browser_locale}</span></span>}
      {rotated && <span className="text-[hsl(var(--warning))]">Session rotated</span>}
      {freshEvent && <span className="text-[hsl(var(--primary))]">Fresh profile</span>}
    </div>
  );
}

/* ---------- Mobile (VMOS) takeover ---------- */
function MobileTakeoverButton({ agentId, padCode }) {
  const [open, setOpen] = React.useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="default" size="sm" data-testid="takeover-button">
          <Smartphone className="mr-1.5 h-4 w-4" /> View device
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="takeover-dialog-mobile">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-4 w-4" /> Mobile device viewer
          </DialogTitle>
          <DialogDescription>
            Live screenshot stream from the bound VMOS phone. Refreshes every 1.5s.
            Read-only — full ADB control comes via Master Agent tasks (Q.2).
          </DialogDescription>
        </DialogHeader>
        {!padCode ? (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-300">
            No VMOS phone bound to this agent yet. Bind one via the Master Agent
            (<code>bind_vmos_phone</code>) or the <code>/api/vmos/phones/{'{padCode}'}/bind/{'{agentId}'}</code> endpoint.
          </div>
        ) : (
          <MobileScreenshotStream agentId={agentId} padCode={padCode} open={open} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function MobileScreenshotStream({ padCode, open }) {
  const [imgUrl, setImgUrl] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [tick, setTick] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const [pollMs, setPollMs] = React.useState(1500);
  // Phase Q.2 — bidirectional control
  const [controlEnabled, setControlEnabled] = React.useState(false);
  const [textInput, setTextInput] = React.useState('');
  const [imgDims, setImgDims] = React.useState({ w: 0, h: 0 }); // natural dims
  const [busy, setBusy] = React.useState(false);
  const dragStartRef = React.useRef(null);
  const dragStartTimeRef = React.useRef(0);
  const imgRef = React.useRef(null);

  React.useEffect(() => {
    if (!open || paused) return undefined;
    let cancelled = false;
    const fetchOne = async () => {
      try {
        const r = await api.post(`/vmos/phones/${padCode}/screenshot`);
        if (cancelled) return;
        if (r.data?.url) {
          setImgUrl(r.data.url);
          setError(null);
        } else {
          setError('VMOS returned no URL — is the device awake?');
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Screenshot fetch failed');
      } finally {
        if (!cancelled) setTick((t) => t + 1);
      }
    };
    fetchOne();
    const id = setInterval(fetchOne, pollMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [open, paused, pollMs, padCode]);

  // Convert a click on the rendered <img> to natural image coordinates,
  // which the backend re-projects onto the device's physical resolution.
  const toNaturalCoords = (e) => {
    const img = imgRef.current;
    if (!img) return null;
    const rect = img.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * (imgDims.w || img.naturalWidth || rect.width);
    const y = ((e.clientY - rect.top) / rect.height) * (imgDims.h || img.naturalHeight || rect.height);
    return { x: Math.round(x), y: Math.round(y) };
  };

  const sendClick = async (x, y) => {
    setBusy(true);
    try {
      await api.post(`/vmos/phones/${padCode}/click`, {
        x, y, image_w: imgDims.w, image_h: imgDims.h,
      });
      toast.success(`Tap @ (${x}, ${y})`, { duration: 1200 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Tap failed');
    } finally {
      setBusy(false);
    }
  };

  const sendSwipe = async (x1, y1, x2, y2, duration_ms) => {
    setBusy(true);
    try {
      await api.post(`/vmos/phones/${padCode}/swipe`, {
        x1, y1, x2, y2, duration_ms,
        image_w: imgDims.w, image_h: imgDims.h,
      });
      toast.success(`Swipe ${duration_ms}ms`, { duration: 1200 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Swipe failed');
    } finally {
      setBusy(false);
    }
  };

  const sendKey = async (keycode, label) => {
    setBusy(true);
    try {
      await api.post(`/vmos/phones/${padCode}/keyevent`, { keycode });
      toast.success(`Key: ${label}`, { duration: 1000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Key failed');
    } finally {
      setBusy(false);
    }
  };

  const sendText = async () => {
    if (!textInput.trim()) return;
    setBusy(true);
    try {
      await api.post(`/vmos/phones/${padCode}/text`, { text: textInput });
      toast.success(`Typed ${textInput.length} chars`, { duration: 1000 });
      setTextInput('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Type failed');
    } finally {
      setBusy(false);
    }
  };

  const onMouseDown = (e) => {
    if (!controlEnabled) return;
    const c = toNaturalCoords(e);
    if (!c) return;
    dragStartRef.current = c;
    dragStartTimeRef.current = Date.now();
  };

  const onMouseUp = (e) => {
    if (!controlEnabled || !dragStartRef.current) return;
    const start = dragStartRef.current;
    const end = toNaturalCoords(e);
    dragStartRef.current = null;
    if (!end) return;
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const dt = Math.max(80, Date.now() - dragStartTimeRef.current);
    if (dist < 12) {
      sendClick(end.x, end.y);
    } else {
      sendSwipe(start.x, start.y, end.x, end.y, Math.min(1500, Math.max(150, dt)));
    }
  };

  return (
    <div className="space-y-3" data-testid="mobile-stream">
      <div className="flex flex-wrap items-center gap-2">
        <div className="rounded border border-white/10 bg-[hsl(var(--surface-2))] px-2 py-1 font-mono text-[10px] text-[hsl(var(--text-3))]">
          pad: <span className="text-[hsl(var(--text-1))]">{padCode}</span>
        </div>
        <div className="rounded border border-white/10 bg-[hsl(var(--surface-2))] px-2 py-1 font-mono text-[10px] text-[hsl(var(--text-3))]">
          frames: <span className="text-[hsl(var(--text-1))]">{tick}</span>
        </div>
        <div className="rounded border border-white/10 bg-[hsl(var(--surface-2))] px-2 py-1 font-mono text-[10px] text-[hsl(var(--text-3))]">
          poll: <span className="text-[hsl(var(--text-1))]">{pollMs}ms</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7"
          onClick={() => setPaused((p) => !p)}
          data-testid="mobile-stream-pause"
        >
          {paused ? <RefreshCw className="mr-1 h-3 w-3" /> : <X className="mr-1 h-3 w-3" />}
          {paused ? 'Resume' : 'Pause'}
        </Button>
        <select
          value={pollMs}
          onChange={(e) => setPollMs(Number(e.target.value))}
          className="rounded border border-white/10 bg-[hsl(var(--surface-2))] px-1.5 py-1 text-[10px] text-[hsl(var(--text-1))]"
          data-testid="mobile-stream-rate"
        >
          <option value={1000}>1.0s</option>
          <option value={1500}>1.5s</option>
          <option value={2000}>2.0s</option>
          <option value={3000}>3.0s</option>
        </select>
        <Button
          variant={controlEnabled ? 'default' : 'secondary'}
          size="sm"
          className="h-7"
          onClick={() => setControlEnabled((v) => !v)}
          data-testid="mobile-control-toggle"
        >
          <Hand className="mr-1 h-3 w-3" />
          {controlEnabled ? 'Control: ON' : 'Control: OFF'}
        </Button>
      </div>
      <div className="rounded-md border border-white/10 bg-black overflow-hidden flex items-center justify-center select-none"
            style={{ minHeight: '50vh' }}>
        {error && !imgUrl && (
          <div className="p-6 text-center text-xs text-amber-300" data-testid="mobile-stream-error">{error}</div>
        )}
        {imgUrl && (
          <img
            ref={imgRef}
            src={imgUrl}
            alt="Mobile device live stream"
            className={`max-h-[60vh] w-auto ${controlEnabled ? 'cursor-crosshair' : ''} ${busy ? 'opacity-90' : ''}`}
            data-testid="mobile-stream-img"
            draggable={false}
            onLoad={(e) => setImgDims({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onContextMenu={(e) => { if (controlEnabled) e.preventDefault(); }}
          />
        )}
        {!error && !imgUrl && (
          <div className="p-6 text-center text-xs text-[hsl(var(--text-3))]">
            <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
            Establishing screenshot stream…
          </div>
        )}
      </div>
      {controlEnabled && (
        <div className="space-y-2 rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-3"
              data-testid="mobile-control-panel">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-medium text-[hsl(var(--text-2))]">Hardware keys:</span>
            <Button size="sm" variant="secondary" className="h-7 px-2 text-[11px]"
                     onClick={() => sendKey(4, 'BACK')} disabled={busy}
                     data-testid="mobile-key-back">Back</Button>
            <Button size="sm" variant="secondary" className="h-7 px-2 text-[11px]"
                     onClick={() => sendKey(3, 'HOME')} disabled={busy}
                     data-testid="mobile-key-home">Home</Button>
            <Button size="sm" variant="secondary" className="h-7 px-2 text-[11px]"
                     onClick={() => sendKey(187, 'APP_SWITCH')} disabled={busy}
                     data-testid="mobile-key-recents">Recents</Button>
            <Button size="sm" variant="secondary" className="h-7 px-2 text-[11px]"
                     onClick={() => sendKey(82, 'MENU')} disabled={busy}
                     data-testid="mobile-key-menu">Menu</Button>
            <Button size="sm" variant="secondary" className="h-7 px-2 text-[11px]"
                     onClick={() => sendKey(224, 'WAKEUP')} disabled={busy}
                     data-testid="mobile-key-wake">Wake</Button>
            <Button size="sm" variant="secondary" className="h-7 px-2 text-[11px]"
                     onClick={() => sendKey(67, 'DEL')} disabled={busy}
                     data-testid="mobile-key-del">Delete</Button>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') sendText(); }}
              placeholder="Type to send to focused field…"
              className="flex-1 rounded bg-[hsl(var(--surface-2))] border border-white/10 px-2 py-1 text-xs"
              data-testid="mobile-text-input"
              disabled={busy}
            />
            <Button size="sm" onClick={sendText}
                     disabled={busy || !textInput.trim()}
                     data-testid="mobile-text-send">Send</Button>
          </div>
          <div className="text-[10px] leading-relaxed text-[hsl(var(--text-3))]">
            Tap-and-release on the image to send a single tap. Drag to swipe (duration follows your hold time).
            Coordinates are auto-projected from the rendered image (<span className="font-mono">{imgDims.w}×{imgDims.h}</span>) onto the device's logical resolution.
          </div>
        </div>
      )}
      {error && imgUrl && (
        <div className="rounded border border-amber-500/30 bg-amber-500/5 p-2 text-[11px] text-amber-300">
          Last fetch failed: {error}. Showing previous frame.
        </div>
      )}
    </div>
  );
}


